/**
 * TASK FORM — Drawer laterale per creazione task.
 *
 * Campi gestiti:
 *  - title (required)
 *  - type + priority (select affiancati)
 *  - associatedRecords (picker polimorfico multi-select: Person/Business/Quotation)
 *  - assignedUserId (lookup utenti via userApi.search)
 *  - queueId (select da state.queues)
 *  - expirationDate: preset "Entro X giorni" + orario (calcolati in ISO al submit)
 *  - remindDate (datetime-local promemoria)
 *  - recurrence: checkbox + (se attiva) valore numerico + unità (TaskRecurrentUnit)
 *  - notes (textarea)
 *
 * Al submit:
 *  1. dispatch createTask(payload) -> Task
 *  2. per ogni record associato: createBidirectionalRelationship(Task <-> record)
 *  3. se linkedEntity presente (aperto da PersonDetail/BusinessDetail),
 *     anche quello viene collegato (oltre agli associatedRecords manuali).
 *  4. reset / chiudi in base a intent ref (submit vs "crea e aggiungi altro").
 */

import { useState, useEffect, useRef, useMemo, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../components/useToast';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import CloseIcon from '@mui/icons-material/Close';
import { useServerPagedSearch, datetimeLocalToIso, formatDate } from '@crm/shared';
import { SidePanel } from '../../components/SidePanel';
import { RichTextEditor } from '../../components/RichTextEditor';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { createTask } from './taskThunks';
import { fetchQueues } from '../queue/queueThunks';
import { fetchUserSettings } from '../../auth/authSlice';
import { userApi } from '../user/userApi';
import { useCurrentGroupId } from '../../auth/authSelectors';
import { getEntityFqn } from '../../entityRegistry/entityRegistry';
import { fetchPipelines } from '../pipeline/pipelineThunks';
import { pipelinesOfType } from '../pipeline/pipelineEntityTypes';
import { pipelineStateLabel, sortedStates } from '../pipeline/pipelineUtils';
import type { PipelineSliceState } from '../pipeline/pipelineTypes';
import { useAssociatedRecordsBinder } from '../../components/useAssociatedRecordsBinder';
import { AssociatedRecordsField } from '../../components/AssociatedRecordsField';
import { DEADLINE_PRESETS, REMINDER_PRESETS, TASK_TYPES, TASK_PRIORITIES, type DeadlinePreset, type ReminderPreset, type TaskCreatePayload, type TaskType, type TaskPriority, type Task } from './taskTypes';
import { TASK_RECURRENT_UNITS, type TaskRecurrentUnit } from './taskRecurrentUnits';
import type { User, UserOption } from '../user/userTypes';
import { PillField, PillOptions } from './PillField';
import { TagGlyph, CalendarGlyph, ClockGlyph, FlagGlyph, ChevronDownGlyph, PlusGlyph } from '../../components/icons';

/** Priorità → classe del pallino colorato (stessa mappa della lista/bacheca). */
const PRIO_DOT: Record<string, string> = { high: 'high', medium: 'med', low: 'low', none: 'low' };

/** Orari selezionabili (ogni 30 min, 24h): la pill "Orario" li mostra come
 *  lista a tendina, così basta un click per scegliere. */
const TIME_OPTIONS: string[] = Array.from({ length: 48 }, (_, i) => {
  const h = String(Math.floor(i / 2)).padStart(2, '0');
  return `${h}:${i % 2 === 0 ? '00' : '30'}`;
});

/** Iniziali (max 2) per l'avatar dell'assegnatario nella pill. */
function initialsOf(name?: string): string {
  if (!name) return '';
  return name.trim().split(/\s+/).slice(0, 2).map((n) => n[0]?.toUpperCase() ?? '').join('');
}


interface TaskFormProps {
  onClose: () => void;
  onCreated?: () => void;
  /** Se presente, precompila il tipo (es. 'chiamata', 'note') nel form. */
  initialType?: TaskType;
  /**
   * Se presente, dopo la create dispatcha una Relationship bidirezionale
   * Task<->linkedEntity cosi' il task risulta collegato al detail da cui
   * e' stato aperto il drawer (es. PersonDetail).
   */
  linkedEntity?: { className: string; classPK: number };
  /**
   * Contenitore di rendering:
   *  - 'drawer' (default): side panel scorrevole da destra. Usato dalla
   *    home Task ("Crea task").
   *  - 'modal': MUI Dialog centrale. Usato dai flussi di creazione
   *    rapida dalle detail (azioni colonna sinistra, bottoni "+ Nuova X"
   *    nelle tab attivita').
   */
  mode?: 'drawer' | 'modal';
}


// Elenco e union dei preset di scadenza vivono in `taskTypes` insieme: separarli lascerebbe l'union
// libera di divergere dai valori dell'array, e un refuso non fallirebbe la compilazione ma
// produrrebbe un task SENZA scadenza (computeExpirationDate non trova il preset e torna null).

const FORM_ID = 'task-form';

/**
 * defaultUserId 0 e' un sentinel di "auth non ancora caricato": il submit
 * resta disabilitato finche' non viene risolto (vedi canSubmit). Non si
 * usa piu' il fallback a 1 hardcoded (ambigo tra utenti reali in prod).
 */
function defaultForm(initialType?: TaskType, defaultUserId?: number): TaskCreatePayload {
  return {
    title: '',
    type: initialType ?? 'activity',
    priority: 'none',
    expirationDate: null,
    remindDate: null,
    recurrent: false,
    recurrentValue: 0,
    recurrentUnit: 'none',
    assignedUserId: defaultUserId ?? 0,
    queueId: null,
    notes: null,
    currentStateId: 0,
  };
}

/** Data odierna in formato YYYY-MM-DD (local date, non ISO UTC). */
function todayDateStr(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** Somma N giorni a una data YYYY-MM-DD, ritorna nuova stringa YYYY-MM-DD. */
function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + days);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** Compone ISO datetime (es. "2026-04-22T17:00:00Z") da preset + time + customDate. */
function computeExpirationDate(preset: DeadlinePreset, time: string, customDate: string): string | null {
  const presetCfg = DEADLINE_PRESETS.find((p) => p.value === preset);
  if (!presetCfg) return null;
  const base = presetCfg.days != null ? addDays(todayDateStr(), presetCfg.days) : customDate;
  if (!base) return null;
  const hhmm = time && /^\d{2}:\d{2}$/.test(time) ? time : '17:00';
  return datetimeLocalToIso(`${base}T${hhmm}`);
}

/**
 * Compone remindDate (ISO `...:00Z`). I preset relativi sottraggono l'offset
 * dalla scadenza (`expirationIso`), quindi seguono la stessa convenzione UTC
 * del deadline; 'none' -> null, 'custom' -> data/ora scelta a mano. Se manca
 * la scadenza, i preset relativi non possono calcolare e tornano null.
 */
function computeRemindDate(preset: ReminderPreset, custom: string, expirationIso: string | null): string | null {
  if (preset === 'none') return null;
  if (preset === 'custom') return datetimeLocalToIso(custom);
  if (!expirationIso) return null;
  const cfg = REMINDER_PRESETS.find((p) => p.value === preset);
  if (!cfg || cfg.offsetMinutes == null) return null;
  const remindMs = new Date(expirationIso).getTime() - cfg.offsetMinutes * 60000;
  return `${new Date(remindMs).toISOString().slice(0, 19)}Z`;
}

export default function TaskForm({ onClose, onCreated, initialType, linkedEntity, mode = 'drawer' }: TaskFormProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const toast = useToast();
  const { items: queues } = useAppSelector((state) => state.queues);
  const currentUserId = useAppSelector((state) => state.auth.user?.userId);
  const currentUserName = useAppSelector((state) => state.auth.user?.fullName);
  const userSettings = useAppSelector((state) => state.auth.userSettings);

  // Le pipeline DI TIPO TASK. Mostrare quelle dei preventivi produrrebbe stati che il task non
  // puo' avere, e la scelta fallirebbe lato server con un 400.
  const allPipelines = useAppSelector((s) => (s.pipelines as PipelineSliceState).items);
  const taskPipelines = useMemo(
    () => pipelinesOfType(allPipelines, getEntityFqn('Task')),
    [allPipelines],
  );
  const [pipelineId, setPipelineId] = useState(0);
  const pipelineStates = useMemo(
    () => sortedStates(taskPipelines.find((x) => x.pipelineId === pipelineId)),
    [taskPipelines, pipelineId],
  );
  const groupId = useCurrentGroupId();

  // Le pipeline servono alla tendina del funnel; si caricano una volta sola.
  useEffect(() => {
    if (allPipelines.length === 0) dispatch(fetchPipelines({ pageSize: 1000 }));
  }, [allPipelines.length, dispatch]);

  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState<TaskCreatePayload>(() => defaultForm(initialType, currentUserId));
  // Focus sul titolo all'apertura (il focus-trap MUI andrebbe altrimenti sulla X).
  const titleRef = useRef<HTMLInputElement>(null);
  // Campo data della scadenza "personalizzata": serve per aprirne il calendario
  // appena si sceglie quella voce.
  const customDateRef = useRef<HTMLInputElement>(null);
  // Rimonta il RichTextEditor (non controllato) per svuotarlo dopo un submit "keep".
  const [editorKey, setEditorKey] = useState(0);

  // Assignee lookup server-paged: il backend users non e' paginato ancora
  // (mock-only), paginiamo client-side il risultato di userApi.search().
  const [assignee, setAssignee] = useState<UserOption | null>(
    currentUserId && currentUserName ? { id: currentUserId, label: currentUserName } : null
  );
  const {
    loading: usersLoading,
    autocompleteProps: userAutocompleteProps,
    listboxProps: userListboxProps,
  } = useServerPagedSearch<UserOption>({
    fetchPage: async (q, page, size) => {
      const list = await userApi.search(q, groupId);
      const start = (page - 1) * size;
      return {
        items: list.slice(start, start + size).map((u: User) => ({ id: u.id, label: u.fullName })),
        totalCount: list.length,
      };
    },
    pageSize: 20,
    debounceMs: 300,
    resetOn: [groupId],
  });

  // Associated records: picker polimorfico server-paged multi-type
  // (Person/Business/Quotation), centralizzato in useAssociatedRecordsBinder.
  const taskFqn = getEntityFqn('Task');

  const {
    records: selectedRecords,
    setRecords: setSelectedRecords,
    applyAtSubmit: applyRecordsAtSubmit,
    pickerProps: recordsPickerProps,
  } = useAssociatedRecordsBinder({
    mode: 'create',
    entityFqn: taskFqn ?? undefined,
    reference: linkedEntity ?? null,
  });

  // Deadline state: preset + orario; se preset === 'custom' usa customDate.
  // Inizializzati dai default-task dell'utente (se già nello store), altrimenti
  // dai default del form; le preset di UserSetting coincidono 1:1 con queste.
  const [deadlinePreset, setDeadlinePreset] = useState<DeadlinePreset>(
    (userSettings?.taskDeadline as DeadlinePreset) ?? 'threeDays');
  const [deadlineTime, setDeadlineTime] = useState(userSettings?.taskTime ?? '17:00');
  const [customDate, setCustomDate] = useState(todayDateStr());

  // Reminder state: preset relativo alla scadenza; se 'custom' usa reminderCustom
  // (datetime-local "YYYY-MM-DDTHH:mm").
  const [reminderPreset, setReminderPreset] = useState<ReminderPreset>(
    (userSettings?.taskReminder as ReminderPreset) ?? 'none');
  const [reminderCustom, setReminderCustom] = useState('');

  // Default-task per-utente: se non sono nello store li carico (lazy) e li applico
  // ai campi UNA volta sola. Se erano già presenti al mount li ho applicati negli
  // initializer (ref parte true) e qui non riapplico (per non sovrascrivere edit).
  const appliedDefaultsRef = useRef(userSettings != null);
  useEffect(() => {
    if (!userSettings) {
      dispatch(fetchUserSettings());
      return;
    }
    if (appliedDefaultsRef.current) return;
    appliedDefaultsRef.current = true;
    setDeadlinePreset(userSettings.taskDeadline as DeadlinePreset);
    setDeadlineTime(userSettings.taskTime);
    setReminderPreset(userSettings.taskReminder as ReminderPreset);
  }, [userSettings, dispatch]);

  // Intent per handleSubmit (close vs keep) — ref per evitare race React.
  const submitIntentRef = useRef<'close' | 'keep'>('close');

  useEffect(() => { dispatch(fetchQueues()); }, [dispatch]);

  const deadlineIso = useMemo(
    () => computeExpirationDate(deadlinePreset, deadlineTime, customDate),
    [deadlinePreset, deadlineTime, customDate]
  );

  // Il promemoria relativo dipende dalla scadenza: si ricalcola quando cambia
  // deadlineIso o la scelta utente.
  const remindDateIso = useMemo(
    () => computeRemindDate(reminderPreset, reminderCustom, deadlineIso),
    [reminderPreset, reminderCustom, deadlineIso]
  );

  // Submit valido solo quando abbiamo un userId reale: o lo ha scelto l'utente
  // dall'Autocomplete (assignee) o e' caricato nello state auth (currentUserId).
  // Senza questi, in precedenza cadevamo su `1` hardcoded -> in prod puntava
  // a un utente a caso (o inesistente -> 404/500).
  const resolvedAssignedUserId = assignee?.id ?? currentUserId ?? null;
  const canSubmit = resolvedAssignedUserId !== null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit || resolvedAssignedUserId === null) return;
    const intent = submitIntentRef.current;
    submitIntentRef.current = 'close'; // reset per click successivi

    const payload: TaskCreatePayload = { ...form, assignedUserId: resolvedAssignedUserId, expirationDate: deadlineIso, remindDate: remindDateIso };

    // Guard: il promemoria non può cadere dopo la scadenza (caso possibile solo
    // con "Personalizzata"). Blocca con toast; la form resta aperta per la correzione.
    if (remindDateIso && deadlineIso && new Date(remindDateIso) > new Date(deadlineIso)) {
      toast({ severity: 'error', message: t('task.reminderAfterDeadline') });
      return;
    }

    setSubmitting(true);
    try {
      // Create del Task: errore qui e' BLOCCANTE — toast + early return
      // (form resta aperta per consentire correzione del payload).
      let created: Task;
      try {
        created = (await dispatch(createTask(payload)).unwrap()) as Task;
      } catch (err) {
        toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
        return;
      }
      if (!taskFqn) {
        // Simmetrico al warn in AddRelationDrawer: senza segnale il task
        // verrebbe creato senza collegamenti e nessuno saprebbe perche'.
        console.warn(
          '[relationship] TaskForm: Task non registrato nel registry — link saltati',
          { selectedRecords: selectedRecords.length }
        );
      }
      if (taskFqn) {
        await applyRecordsAtSubmit?.(taskFqn, created.taskId);
      }
      toast({ severity: 'success', message: t('common.saved') });
      onCreated?.();
      if (intent === 'keep') {
        // Reset completo per il task successivo, preservando solo l'assignee
        // (probabile stesso utente). Anche scadenza e promemoria tornano ai
        // default: sono stati separati da `form`, quindi vanno azzerati a mano.
        setForm(defaultForm(initialType, currentUserId));
        setEditorKey((k) => k + 1);
        setSelectedRecords?.([]);
        setDeadlinePreset((userSettings?.taskDeadline as DeadlinePreset) ?? 'threeDays');
        setDeadlineTime(userSettings?.taskTime ?? '17:00');
        setCustomDate(todayDateStr());
        setReminderPreset((userSettings?.taskReminder as ReminderPreset) ?? 'none');
        setReminderCustom('');
      } else {
        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  }


  // Footer come da mockup: "Crea e aggiungi" a sinistra (testo), poi Annulla +
  // Crea task a destra (margin-right:auto sul primo li separa — vedi .tc-addanother).
  const footer = (
    <>
      <Button
        type="submit"
        form={FORM_ID}
        onClick={() => { submitIntentRef.current = 'keep'; }}
        variant="text"
        startIcon={<PlusGlyph width={15} height={15} />}
        disabled={submitting || !canSubmit}
        className="tc-addanother"
      >
        {t('task.createAndAddAnother')}
      </Button>
      <Button type="button" onClick={onClose} variant="outlined">
        {t('common.cancel')}
      </Button>
      <Button
        type="submit"
        form={FORM_ID}
        onClick={() => { submitIntentRef.current = 'close'; }}
        variant="contained"
        disabled={submitting || !canSubmit}
      >
        {t('task.createTask')}
      </Button>
    </>
  );

  // Etichette derivate per le pill (nome campo sempre visibile + valore).
  const typeLabel = t(TASK_TYPES.find((x) => x.value === form.type)?.label ?? '');
  const prioEntry = TASK_PRIORITIES.find((x) => x.value === form.priority);
  const prioLabel = prioEntry ? t(prioEntry.label) : '';
  // Con la scadenza personalizzata la pill mostra la data scelta, non la parola
  // "personalizzata": il popover ora si chiude appena si sceglie il giorno,
  // quindi la pill e' l'unico posto dove leggerlo senza riaprire.
  const deadlineLabel = deadlinePreset === 'custom' && customDate
    ? formatDate(customDate)
    : t(DEADLINE_PRESETS.find((p) => p.value === deadlinePreset)?.label ?? '');
  const assigneeLabel = assignee?.label ?? currentUserName ?? '';

  // Layout "progressive disclosure":
  //  - Titolo eroe (input grande, required)
  //  - Riga pill: Tipo · Scadenza · Orario · Priorità · Assegnato (ognuna apre
  //    il controllo reale in un popover)
  //  - "Più opzioni": Promemoria, Coda, Ripetizione (checkbox→campi), Note
  //  - Associazioni: link (AssociatedRecordsField, invariato)
  const formContent = (
      <form id={FORM_ID} onSubmit={handleSubmit} className="task-create">
        <input
          ref={titleRef}
          className="task-create__title"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder={t('task.titlePlaceholder')}
          aria-label={t('task.taskTitle')}
          required
        />

        <div className="task-create__quick">
          {/* Tipo: indica COSA ricordarsi di fare (telefonare, whatsapp, ...);
              scelta legittima in creazione, diversa dal registrare un evento. */}
          <PillField icon={<TagGlyph />} label={t('task.taskType')} value={typeLabel}>
            {(close) => (
              <PillOptions
                options={TASK_TYPES.map((x) => ({ value: x.value, label: t(x.label) }))}
                selected={form.type}
                onSelect={(v) => setForm({ ...form, type: v as TaskType })}
                close={close}
              />
            )}
          </PillField>

          <PillField icon={<CalendarGlyph />} label={t('task.expirationDate')} value={deadlineLabel} popoverWidth={300}>
            {(close) => (
              <PillOptions
                options={DEADLINE_PRESETS.map((p) => ({
                  value: p.value,
                  label: t(p.label),
                  // La data sta di fianco a "personalizzata", non sotto la lista:
                  // scegliere la voce e scegliere il giorno sono un gesto solo.
                  // Prima la voce chiudeva il popover e il campo data si vedeva
                  // solo riaprendolo.
                  extra: p.value === 'custom' ? (
                    <input
                      ref={customDateRef}
                      type="date"
                      className="tf-date"
                      value={customDate}
                      aria-label={t(p.label)}
                      onFocus={() => setDeadlinePreset('custom')}
                      onChange={(e) => {
                        setDeadlinePreset('custom');
                        setCustomDate(e.target.value);
                        close();
                      }}
                    />
                  ) : undefined,
                }))}
                selected={deadlinePreset}
                onSelect={(v) => {
                  setDeadlinePreset(v as DeadlinePreset);
                  // "Personalizzata" apre subito il calendario del campo
                  // affiancato: niente secondo click. `showPicker` non c'e' su
                  // tutti i browser (e vuole un gesto abilitante): dove non va,
                  // il campo resta a fuoco, che e' comunque il passo successivo.
                  if (v === 'custom') {
                    const input = customDateRef.current;
                    input?.focus();
                    try { input?.showPicker?.(); } catch { /* resta il focus */ }
                  }
                }}
                close={close}
              />
            )}
          </PillField>

          <PillField icon={<ClockGlyph />} label={t('task.deadlineTime')} value={deadlineTime} popoverWidth={130}>
            {(close) => (
              <PillOptions
                options={(TIME_OPTIONS.includes(deadlineTime) ? TIME_OPTIONS : [deadlineTime, ...TIME_OPTIONS])
                  .map((tm) => ({ value: tm, label: tm }))}
                selected={deadlineTime}
                onSelect={(v) => setDeadlineTime(v)}
                close={close}
              />
            )}
          </PillField>

          <PillField
            icon={<FlagGlyph />} label={t('task.priority')}
            value={<><i className={`pdot ${PRIO_DOT[form.priority] ?? 'med'}`} />{prioLabel}</>}
          >
            {(close) => (
              <PillOptions
                options={TASK_PRIORITIES.map((p) => ({
                  value: p.value,
                  label: <span className="tf-prio-opt"><i className={`pdot ${PRIO_DOT[p.value] ?? 'med'}`} />{t(p.label)}</span>,
                }))}
                selected={form.priority}
                onSelect={(v) => setForm({ ...form, priority: v as TaskPriority })}
                close={close}
              />
            )}
          </PillField>

          <PillField
            icon={<span className="tf-av">{initialsOf(assigneeLabel) || '?'}</span>}
            label={t('task.assignedTo')}
            value={assigneeLabel || t('common.none')}
            popoverWidth={280}
          >
            {() => (
              <Autocomplete
                {...userAutocompleteProps}
                value={assignee}
                onChange={(_, val) => setAssignee(val)}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                loading={usersLoading}
                slotProps={{ listbox: userListboxProps }}
                renderInput={(params) => <TextField {...params} label={t('task.assignedTo')} autoFocus />}
                size="small"
                openOnFocus
              />
            )}
          </PillField>
        </div>

        <details className="task-create__more">
          <summary><ChevronDownGlyph className="chv" /> {t('task.moreOptions')}</summary>
          <div className="task-create__more-body">
            {/* Promemoria e Coda affiancati su una riga (Promemoria impila il
                campo custom sotto di sé, così la riga resta a due colonne). */}
            <div className="tc-row2">
              <div className="tc-cell">
                <TextField
                  select label={t('task.remindDate')} value={reminderPreset}
                  onChange={(e) => setReminderPreset(e.target.value as ReminderPreset)}
                  fullWidth size="small" className="form-field"
                >
                  {REMINDER_PRESETS.map((r) => <MenuItem key={r.value} value={r.value}>{t(r.label)}</MenuItem>)}
                </TextField>
                {reminderPreset === 'custom' && (
                  <TextField
                    type="datetime-local" value={reminderCustom}
                    onChange={(e) => setReminderCustom(e.target.value)}
                    slotProps={{ inputLabel: { shrink: true } }}
                    fullWidth size="small" className="form-field"
                  />
                )}
              </div>
              <TextField
                select label={t('task.queue')} value={form.queueId ?? ''}
                onChange={(e) => setForm({ ...form, queueId: e.target.value ? Number(e.target.value) : null })}
                fullWidth size="small" className="form-field"
              >
                <MenuItem value="">{t('common.none')}</MenuItem>
                {queues.map((q) => <MenuItem key={q.queueId} value={q.queueId}>{q.name}</MenuItem>)}
              </TextField>
            </div>

            {/* Collocazione in pipeline: cascata pipeline -> stato, come nel form del preventivo ma
                NON obbligatoria. Un task senza stato di pipeline e' normale, e imporglielo
                replicherebbe una regola che e' del preventivo.

                Riga propria e non accanto a promemoria/coda: sono due tendine legate fra loro, e
                spezzarle su righe diverse nasconderebbe che la seconda dipende dalla prima.

                Il blocco compare solo se esiste almeno una pipeline di tipo Task: una tendina vuota
                e' peggio di una tendina assente. */}
            {taskPipelines.length > 0 && (
              <div className="tc-row2">
                <TextField
                  select label={t('task.pipeline')} value={pipelineId || ''}
                  onChange={(e) => {
                    const next = e.target.value === '' ? 0 : Number(e.target.value);

                    setPipelineId(next);
                    // Cambiando pipeline lo stato scelto prima non esiste piu': azzerarlo e'
                    // l'unico modo di non mandare al server uno stato di un'altra pipeline.
                    setForm({ ...form, currentStateId: 0 });
                  }}
                  fullWidth size="small" className="form-field"
                >
                  <MenuItem value="">{t('common.none')}</MenuItem>
                  {taskPipelines.map((p) => (
                    <MenuItem key={p.pipelineId} value={p.pipelineId}>{p.name}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select label={t('task.pipelineState')} value={form.currentStateId || ''}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      currentStateId: e.target.value === '' ? 0 : Number(e.target.value),
                    })
                  }
                  disabled={pipelineId <= 0}
                  fullWidth size="small" className="form-field"
                >
                  <MenuItem value="">{t('common.none')}</MenuItem>
                  {pipelineStates.map((st) => (
                    <MenuItem key={st.pipelineStateId} value={st.pipelineStateId}>
                      {pipelineStateLabel(st)}
                    </MenuItem>
                  ))}
                </TextField>
              </div>
            )}

            <div className="task-form__recurrence">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.recurrent}
                    onChange={(e) => {
                      const recurrent = e.target.checked;
                      setForm({
                        ...form,
                        recurrent,
                        recurrentUnit: recurrent
                          ? (form.recurrentUnit === 'none' ? 'day' : form.recurrentUnit)
                          : 'none',
                        recurrentValue: recurrent ? (form.recurrentValue > 0 ? form.recurrentValue : 1) : 0,
                      });
                    }}
                  />
                }
                label={t('task.setRecurrence')}
              />
              {form.recurrent && (
                <div className="task-form__recurrence-expand">
                  <TextField
                    type="number" label={t('task.recurrentValue')} value={form.recurrentValue}
                    onChange={(e) => setForm({ ...form, recurrentValue: Number(e.target.value) || 1 })}
                    slotProps={{ htmlInput: { min: 1 } }}
                    className="form-field"
                  />
                  <TextField
                    select label={t('task.recurrentUnit')} value={form.recurrentUnit}
                    onChange={(e) => setForm({ ...form, recurrentUnit: e.target.value as TaskRecurrentUnit })}
                    fullWidth className="form-field"
                  >
                    {TASK_RECURRENT_UNITS.map((u) => <MenuItem key={u.value} value={u.value}>{t(u.label)}</MenuItem>)}
                  </TextField>
                </div>
              )}
            </div>

            <div className="form-field">
              <label className="field-row__label text-xs text-muted">{t('common.notes')}</label>
              <RichTextEditor
                key={editorKey}
                onChange={(html) => setForm({ ...form, notes: html || null })}
                placeholder={t('task.notesPlaceholder')}
              />
            </div>
          </div>
        </details>

        {/* Associazioni: resta il link (picker polimorfico Person/Business +
            entità plugin), invariato rispetto alla schermata di modifica. */}
        <div className="task-create__assoc">
          <AssociatedRecordsField
            {...recordsPickerProps}
            records={selectedRecords}
            onChange={(val) => setSelectedRecords?.(val)}
            displayMode="click-to-edit"
          />
        </div>
      </form>
  );

  if (mode === 'modal') {
    // Stili del modal sono definiti in _components.scss sotto .app-dialog*.
    // Nessun sx MUI: il designer puo' ritoccare spaziature/colori senza
    // toccare TSX. Header allineato a SidePanel (primary + testo chiaro).
    return (
      <Dialog
        open
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        className="app-dialog"
        // Focus sul titolo a fine transizione (dopo che il focus-trap MUI si è
        // assestato), così l'utente scrive subito. In MUI v9 la transizione è
        // uno slot (slotProps.transition), non più TransitionProps.
        slotProps={{ transition: { onEntered: () => titleRef.current?.focus() } }}
      >
        {/* Header chiaro con kicker (nuovo design): niente barra brand, il
            titolo del task è l'input eroe dentro il body. */}
        <DialogTitle className="tc-head">
          <span className="tc-kicker">{t('task.newTask')}</span>
          <IconButton
            aria-label={t('common.cancel')}
            onClick={onClose}
            size="small"
            className="tc-x"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent className="app-dialog__body">{formContent}</DialogContent>
        <DialogActions className="app-dialog__footer">{footer}</DialogActions>
      </Dialog>
    );
  }

  return (
    <SidePanel title={t('task.createTask')} onClose={onClose} footer={footer}>
      {formContent}
    </SidePanel>
  );
}
